// src/components/ui/TaskTableView.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import BadgeDropdown from './BadgeDropdown';
import CustomDropdown from './CustomDropdown';

// Filter options removed - now using unified header controls

// --- Badge Styling Helper ---
const getBadgeClass = (type, value) => {
  if (type === 'priority') {
    switch (value) {
      case 'High': return 'bg-red-500/20 text-red-400 border border-red-500/30';
      case 'Medium': return 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30';
      case 'Low': return 'bg-green-500/20 text-green-400 border border-green-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  }
  if (type === 'domain') {
    switch (value) {
      case 'Software': return 'bg-purple-500/20 text-purple-400 border border-purple-500/30';
      case 'Electronics': return 'bg-blue-500/20 text-blue-400 border border-blue-500/30';
      case 'Mechanical': return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
      default: return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  }
  if (type === 'status') {
    switch (value) {
      // Done category - green
      case 'Done':
      case 'Solved':
      case 'completed':
        return 'bg-green-500/20 text-green-400 border border-green-500/30';
      // In Progress category - orange/blue
      case 'In Progress':
      case 'pending':
        return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
      // Not Decided category - gray
      case 'Not Decided':
      case 'new':
      case 'under_discussion':
      case 'solution_decided':
      default:
        return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
    }
  }
  return 'bg-gray-500/20 text-gray-400 border border-gray-500/30';
};

const TaskTableView = ({ problems = [] }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- State for Click-to-Edit ---
  // Will store { rowId: '...', column: 'status' | 'assignee' | 'deadline' }
  const [editingCell, setEditingCell] = useState(null);

  const navigate = useNavigate();

  // Convert problems to tasks format for compatibility
  const tasks = problems.map(problem => ({
    id: problem.id,
    title: problem.title,
    status: problem.status,
    domain: problem.domain,
    priority: problem.priority,
    deadline_date: problem.due_date, // Map due_date to deadline_date
    assigned_user: problem.assigned_user || null
  }));

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);

      // Only fetch users data - problems data comes from props
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email');

      if (userError) {
        console.error('Error fetching users:', userError);
        setError(userError.message);
      } else {
        setUsers(userData);
      }

      setLoading(false);
    };

    fetchUsers();
  }, []); // Only fetch users once

  // --- UPDATED HANDLERS ---
  // They now update the database and close the editor
  // Parent component will handle state updates through re-fetching
  const handleUpdateStatus = async (problemId, newStatus) => {
    setEditingCell(null); // Close editor
    const { error } = await supabase
      .from('problems')
      .update({ status: newStatus })
      .eq('id', problemId);

    if (error) {
      console.error('Error:', error);
      alert('Failed to update status.');
    }
    // Note: Parent component (Dashboard) will handle state updates
  };

  const handleAssignUser = async (problemId, newUserId) => {
    setEditingCell(null); // Close editor
    const userIdToSet = newUserId === "" ? null : newUserId;

    const { error } = await supabase
      .from('problems')
      .update({ assigned_to: userIdToSet })
      .eq('id', problemId);

    if (error) {
      console.error('Error:', error);
      alert('Failed to assign user.');
    }
    // Note: Parent component (Dashboard) will handle state updates
  };

  const handleSetDeadline = async (problemId, newDate) => {
    setEditingCell(null); // Close editor
    const { error } = await supabase
      .from('problems')
      .update({ deadline_date: newDate })
      .eq('id', problemId);

    if (error) {
      console.error('Error:', error);
      alert('Failed to set deadline.');
    }
    // Note: Parent component (Dashboard) will handle state updates
  };

  const handleTitleClick = (problemId) => {
    navigate('/tasks', { state: { openProblemId: problemId } });
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

  // --- Helper to render the styled badge/text ---
  const renderCellContent = (task, column) => {
    if (column === 'status') {
      return (
        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getBadgeClass('status', task.status)}`}>
          {task.status || 'N/A'}
        </span>
      );
    }
    if (column === 'assignee') {
      return (
        <span className="text-gray-200">
          {task.assigned_user ? task.assigned_user.email : <span className="text-gray-400">Unassigned</span>}
        </span>
      );
    }
    if (column === 'deadline') {
      return (
        <span className="text-gray-200">
          {task.deadline_date ? format(new Date(task.deadline_date), 'MMM dd, yyyy') : <span className="text-gray-400">No deadline</span>}
        </span>
      );
    }
  };

  // --- Main component render ---
  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* --- Table --- */}
      <div className="glass-card overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600">
          <thead className="bg-gray-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Problem Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Domain
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Assigned To
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Deadline
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-900/20 divide-y divide-gray-600">
            {tasks.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-400">
                  No tasks found.
                </td>
              </tr>
            )}
            {tasks.map((task) => (
              <tr key={task.id} className="hover:bg-gray-800/30 transition-colors">
                {/* Title Cell (Link) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <div
                    className="text-sm font-medium text-indigo-400 hover:text-indigo-300 hover:underline cursor-pointer"
                    onClick={() => handleTitleClick(task.id)}
                  >
                    {task.title}
                  </div>
                </td>

                {/* Status Cell (Click-to-Edit) */}
                <td 
                  className="px-6 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-800/50" 
                  onClick={() => setEditingCell({ rowId: task.id, column: 'status' })}
                >
                  {editingCell?.rowId === task.id && editingCell?.column === 'status' ? (
                    <div className="relative z-10">
                      <BadgeDropdown
                        value={task.status}
                        onChange={(value) => handleUpdateStatus(task.id, value)}
                        type="status"
                        options={[
                          // Not Decided category
                          { value: 'Not Decided', label: 'Not Decided' },
                          { value: 'new', label: 'New' },
                          { value: 'under_discussion', label: 'Under Discussion' },
                          { value: 'solution_decided', label: 'Solution Decided' },
                          // In Progress category  
                          { value: 'In Progress', label: 'In Progress' },
                          { value: 'pending', label: 'Pending' },
                          // Done category
                          { value: 'Done', label: 'Done' },
                          { value: 'Solved', label: 'Solved' },
                          { value: 'completed', label: 'Completed' }
                        ]}
                        className="min-w-[140px]"
                      />
                    </div>
                  ) : (
                    renderCellContent(task, 'status')
                  )}
                </td>

                {/* Domain Cell (Static) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getBadgeClass('domain', task.domain)}`}>
                    {task.domain || 'N/A'}
                  </span>
                </td>

                {/* Priority Cell (Static) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 inline-flex text-xs leading-5 font-medium rounded-full ${getBadgeClass('priority', task.priority)}`}>
                    {task.priority || 'N/A'}
                  </span>
                </td>

                {/* Assignee Cell (Click-to-Edit) */}
                <td 
                  className="px-6 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-800/50" 
                  onClick={() => setEditingCell({ rowId: task.id, column: 'assignee' })}
                >
                  {editingCell?.rowId === task.id && editingCell?.column === 'assignee' ? (
                    <div className="relative z-10">
                      <CustomDropdown
                        value={task.assigned_user ? task.assigned_user.id : ''}
                        onChange={(value) => handleAssignUser(task.id, value)}
                        options={[
                          { value: '', label: 'Unassigned' },
                          ...users.map(user => ({ value: user.id, label: user.email }))
                        ]}
                        className="min-w-[160px]"
                      />
                    </div>
                  ) : (
                    renderCellContent(task, 'assignee')
                  )}
                </td>

                {/* Deadline Cell (Click-to-Edit) */}
                <td 
                  className="px-6 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-800/50" 
                  onClick={() => setEditingCell({ rowId: task.id, column: 'deadline' })}
                >
                  {editingCell?.rowId === task.id && editingCell?.column === 'deadline' ? (
                    <input
                      type="date"
                      autoFocus
                      value={task.deadline_date || ''}
                      onBlur={() => setEditingCell(null)}
                      onChange={(e) => handleSetDeadline(task.id, e.target.value)}
                      className="glass-input text-sm"
                    />
                  ) : (
                    renderCellContent(task, 'deadline')
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TaskTableView;